import React, {
  Dispatch,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  InitialTableState,
  Row,
  SortingState,
  Table,
  TableState,
  useReactTable,
} from "@tanstack/react-table";
import { GraphQLError } from "graphql";
import { clamp, isEmpty, isEqual, isNil, omit, toArray } from "lodash";
import { useHistory, useLocation } from "react-router";
import { buildLink } from "common/routing";
import { ActionBarConfig } from "common/utils/actionBar";
import { checkSavedViewValidVisibility } from "common/utils/queryBuilder";
import {
  ClientSideConfigEntry,
  FilterDescription,
  QueryDescription,
  SyntheticFieldDescription,
} from "common/utils/queryBuilder";
import { pluralize } from "common/utils/strings";
import {
  DataSourceComputations,
  useGetSavedViewDependenciesQuery,
} from "../../../generated/graphql";
import {
  buildLocalTableInfo,
  useLocalTableDisplayConfig,
} from "../../../hooks/useTableDisplayConfig";
import { borderStyles, CSS } from "../../../stitches.config";
import { track } from "../../../utils/tracking";
import { AuthContext } from "../../Authorization/AuthContext";
import { PUBLIC_WIDTH } from "../../Guest/__styles__/Layout";
import { FlexRow } from "../__styles__/Layout";
import { ACTION_COLUMN_DEF_CONSTANTS } from "../ActionCell";
import { BulkOperationProgressBanner } from "../BulkOperationProgressBanner/BulkOperationProgressBanner";
import { Button } from "../Button";
import { Loading } from "../Icons/Loading";
import { Icon } from "../Icons/LucideIcons";
import {
  ColumnSortArray,
  ManualPaginationConfig,
  QueryDescriptionReducerAction,
} from "../Tables/hooks";
import PaginationSection from "../Tables/PaginationSection";
import {
  TableRowsSelectionProvider,
  useTableRowsSelectionState,
} from "../Tables/RowSelectionProvider";
import {
  DEFAULT_PAGE_SIZE,
  getCellMinWidth,
  LoadingDetails,
} from "../Tables/utils";
import { Body } from "../Typography";
import {
  CellDiv,
  ColumnGradient,
  LoadingOverlay,
  NoDataOverlay,
  Resizer,
  StyledTable,
  TableFooter,
  TableHeader,
} from "./__styles__/FullWidthTable";
import { ActionBarWrapper } from "./ActionBarWrapper";
import { DataSyncBanner } from "./DataSyncBanner";
import { DropdownMenu } from "./DropdownMenu";
import { ScrollContainer, ScrollContainerContext } from "./ScrollContainer";
import { TableContext } from "./TableContext";
import { ColumnsButton } from "./TableSettings/Columns";
import { Filter as NewFilters } from "./TableSettings/Filters";
import { SavedViewsButton } from "./TableSettings/SavedViews";
import { SaveButton } from "./TableSettings/SavedViews/SaveButton";
import { SavedView } from "./TableSettings/utils";
import { TimeoutErrorState } from "./TimeoutErrorState";
import { Attribute } from "./types";
import { sanityCheckColumnSizing, sanityCheckSorting } from "./utils";

type SupportedBulkOperationObjectType =
  | "documentUpload"
  | "property"
  | "submission"
  | "task";

const SUPPORTED_BULK_OPERATIONS_TABLES: readonly SupportedBulkOperationObjectType[] =
  ["documentUpload", "property", "submission", "task"] as const;

const isSupportedBulkOperationObjectType = (
  objectType: string,
): objectType is SupportedBulkOperationObjectType => {
  return SUPPORTED_BULK_OPERATIONS_TABLES.includes(
    objectType as SupportedBulkOperationObjectType,
  );
};

interface GradientProps {
  index: number;
  columnsLength: number;
  scrolledToLeft: boolean;
  scrolledToRight: boolean;
}

const Gradient = ({
  index,
  columnsLength,
  scrolledToLeft,
  scrolledToRight,
}: GradientProps) => {
  const getGradientDirection = (index: number) => {
    if (index === 0 && !scrolledToLeft) {
      return "leftToRight";
    } else if (index === columnsLength - 1 && !scrolledToRight) {
      return "rightToLeft";
    } else {
      return "none";
    }
  };

  return (
    <ColumnGradient
      direction={getGradientDirection(index)}
      className="gradient"
    />
  );
};

interface TableProps<T> {
  data: Array<T>;
  columns: Array<ColumnDef<T>>;
  isPublic: boolean;
  table: Table<T>;
  innerTableState: TableState;
  columnSizing: { [key: string]: number };
  defaultColumn: { size: number; maxSize: number; minSize: number };
  setLocalColumnOrder: (order: string[]) => void;
  columnOrder: string[];
  interactiveHeaders: boolean;
  openDropdownId: string | null;
  setOpenDropdownId: (id: string | null) => void;
  setColumnOrder: (order: string[] | ((prev: string[]) => string[])) => void;
  updateQueryDescription?: Dispatch<QueryDescriptionReducerAction>;
  onColumnChange: (columnId: string, action: "add" | "remove") => void;
  tableRef: React.RefObject<HTMLTableElement>;
}

const TableComponent = <T,>({
  data,
  columns,
  isPublic,
  table,
  innerTableState,
  columnSizing,
  defaultColumn,
  setLocalColumnOrder,
  columnOrder,
  interactiveHeaders,
  openDropdownId,
  setOpenDropdownId,
  setColumnOrder,
  updateQueryDescription,
  onColumnChange,
  tableRef,
}: TableProps<T>) => {
  const { scrolledToLeft, scrolledToRight, handleScroll } = useContext(
    ScrollContainerContext,
  );

  const showBottomBorder = data.length < DEFAULT_PAGE_SIZE;

  useEffect(() => {
    setLocalColumnOrder(columnOrder);
  }, [columnOrder, setLocalColumnOrder]);

  const columnSizeVars = React.useMemo(() => {
    const headers = table.getFlatHeaders();
    const colSizes: { [key: string]: number | string } = {};

    for (let i = 0; i < headers.length; i++) {
      const header = headers[i]!;

      //We keep the last column at 100%, since it needs to take up all remaining available space in table width
      const isLastColumn = i === headers.length - 1;
      if (isLastColumn) {
        colSizes[header.id] = isPublic ? "100px" : "100%";
      } else {
        const savedSize = columnSizing[header.id];
        const defaultSize =
          savedSize ? savedSize : (
            (header.column.columnDef.size ?? defaultColumn.size)
          );
        const newSize = innerTableState.columnSizing[header.id] ?? defaultSize;

        const min = header.column.columnDef.minSize ?? defaultColumn.minSize;
        const max = header.column.columnDef.maxSize ?? defaultColumn.maxSize;

        const size = clamp(newSize, min, max);

        colSizes[header.id] = size;
      }
    }

    return colSizes;
  }, [
    innerTableState.columnSizing,
    defaultColumn,
    columnSizing,
    isPublic,
    table,
  ]);

  useLayoutEffect(() => {
    handleScroll();
  }, [columnSizeVars, handleScroll]);

  const TableBody = ({ table }: { table: Table<T> }) => {
    return (
      <tbody>
        {table.getRowModel().rows.map(row => {
          return (
            <tr key={row.id} data-testid="row">
              {row.getVisibleCells().map((cell, index) => {
                const value = cell.getValue();
                const hoverText = typeof value === "string" ? value : "";
                return (
                  <td
                    key={cell.id}
                    style={{
                      width: columnSizeVars[cell.column.id],
                      minWidth: getCellMinWidth<T>(cell),
                    }}
                  >
                    <CellDiv
                      isActionColumn={
                        cell.column.id === ACTION_COLUMN_DEF_CONSTANTS.id
                      }
                      title={hoverText}
                    >
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </CellDiv>
                    <Gradient
                      index={index}
                      columnsLength={columns.length}
                      scrolledToLeft={scrolledToLeft}
                      scrolledToRight={scrolledToRight}
                    />
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    );
  };

  const MemoizedTableBody = React.memo<typeof TableBody>(
    TableBody,
    (prev, next) => prev.table.options.data === next.table.options.data,
  );
  const hasSelectColumn = columns.length > 0 && columns[0]?.id === "select";

  return (
    <StyledTable
      isPublic={isPublic}
      hasSelectColumn={hasSelectColumn}
      ref={tableRef}
      style={{
        borderBottom: showBottomBorder ? `${borderStyles.solid}` : "unset",
        width: isPublic ? PUBLIC_WIDTH : table.getTotalSize(),
        ...columnSizeVars,
      }}
    >
      <thead>
        {table.getHeaderGroups().map(headerGroup => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header, index, array) => {
              const isActionColumn =
                header.id === ACTION_COLUMN_DEF_CONSTANTS.id;
              const isLastColumn = index === array.length - 1;
              const orderableClass =
                interactiveHeaders && !isLastColumn ? "order-pointer" : "";
              const sortableClass =
                header.column.getCanSort() ? "sort-pointer" : "";
              const sortDirectionClass =
                {
                  asc: "sort-asc",
                  desc: "sort-desc",
                }[header.column.getIsSorted() as string] ?? "";

              const { tooltip, className: additionalClasses = "" } =
                header.column.columnDef.meta ?? {};

              const columnHeader = header.column.columnDef.header;
              const hoverText =
                tooltip ? tooltip
                : typeof columnHeader === "string" ? columnHeader
                : "";

              return (
                <th
                  // This attribute is used to return focus to the header when
                  // the dropdown is closed.
                  data-header-id={header.id}
                  key={header.id}
                  role="columnheader"
                  className={`${orderableClass} ${sortableClass} ${sortDirectionClass} ${additionalClasses}`}
                  style={{
                    width: columnSizeVars[header.id],
                    minWidth: getCellMinWidth<T>(header),
                  }}
                  onKeyPress={event => {
                    if (event.key === "Enter" || event.key === " ") {
                      interactiveHeaders &&
                        setOpenDropdownId(
                          openDropdownId === header.id ? null : header.id,
                        );
                    }
                  }}
                  onClick={e => {
                    // Don't open dropdown if click is from resizer
                    if (
                      e.target instanceof HTMLElement &&
                      (e.target.classList.contains("resizer") ||
                        header.id === "select")
                    ) {
                      return;
                    }
                    interactiveHeaders &&
                      setOpenDropdownId(
                        openDropdownId === header.id ? null : header.id,
                      );
                  }}
                  tabIndex={
                    (
                      (header.column.getCanSort() || interactiveHeaders) &&
                      !isActionColumn
                    ) ?
                      0
                    : undefined
                  }
                  data-testid={`header-${header.id}`}
                  data-tooltip={hoverText}
                >
                  <div>
                    {tooltip && (
                      <Icon
                        iconName="info"
                        size={12}
                        color={"contentSecondary"}
                        className="info-icon"
                        style={{
                          marginRight: "8px",
                          top: "1px",
                          position: "relative",
                        }}
                      />
                    )}
                    {header.isPlaceholder ? null : (
                      flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )
                    )}
                  </div>
                  {/* we don't include the resizer on the last column since it needs to take up the rest of the available table space */}
                  {!isLastColumn && (
                    <Resizer
                      key={header.id}
                      onMouseDown={e => {
                        e.preventDefault();
                        header.getResizeHandler()(e);
                      }}
                      onTouchStart={e => {
                        e.preventDefault();
                        header.getResizeHandler()(e);
                      }}
                      onDoubleClick={e => {
                        e.preventDefault();
                        header.column.resetSize();
                      }}
                      className={`resizer`}
                      isResizing={header.column.getIsResizing()}
                      data-testid={`resizer-${header.id}`}
                    />
                  )}
                  <Gradient
                    index={index}
                    columnsLength={columns.length}
                    scrolledToLeft={scrolledToLeft}
                    scrolledToRight={scrolledToRight}
                  />
                  <DropdownMenu
                    header={header}
                    setColumnOrder={setColumnOrder}
                    open={openDropdownId === header.id && !isLastColumn}
                    setOpen={isOpen =>
                      setOpenDropdownId(isOpen ? header.id : null)
                    }
                    updateQueryDescription={updateQueryDescription}
                    removeColumn={id => onColumnChange(id, "remove")}
                  />
                </th>
              );
            })}
          </tr>
        ))}
      </thead>
      <MemoizedTableBody table={table} />
    </StyledTable>
  );
};

export interface FullWidthTableProps<T> {
  previousData?: Array<T>;
  currentData: Array<T>;
  columns: Array<ColumnDef<T>>;
  initialState?: InitialTableState & { filters?: Array<FilterDescription> };
  loadingDetails: LoadingDetails;
  manualPaginationConfig?: ManualPaginationConfig;
  actions?: React.ReactNode;
  prevLocation?: string;
  filterable?: {
    filterConfiguration?: Array<Attribute>;
    search: (params: {
      filters: Array<FilterDescription>;
      sort: SortingState;
      page: number;
      columns: Array<ColumnDef<T>>;
    }) => void;
  };
  interactiveHeaders?: boolean;
  rowCanExpand?: (row: Row<T>) => boolean;
  excludePaginationNav?: boolean;
  withoutSideNav?: boolean;
  columnSettingProps?: Maybe<{
    columnConfiguration: Attribute[];
    columnDefinitions: ColumnDef<T>[];
  }>;
  timeoutError?: Maybe<GraphQLError>;
  queryDescription?: QueryDescription;
  currentView?: Maybe<SavedView>;
  updateQueryDescription?: Dispatch<QueryDescriptionReducerAction>;
  refetchSavedViews: () => void;
  refetchTable: () => void;
  savedViews?: Array<SavedView>;
  clientSideColumnConfig?: Array<ClientSideConfigEntry>;
  dataSourceComputationsAffectingQuery?: Array<DataSourceComputations>;
  rowIdField: string;
  totalCount: number;
  actionBarConfig?: ActionBarConfig;
  filterExclusionMatcher?: (filterDescription: FilterDescription) => boolean;
  additionalFilters?: Array<FilterDescription>;
  hideViewOnMapButton?: boolean;
}

const FullWidthTableBase = <T,>({
  previousData,
  currentData,
  columns: initialColumns,
  initialState = {
    pagination: {
      pageIndex: 0,
      pageSize: DEFAULT_PAGE_SIZE,
    },
  },
  loadingDetails,
  manualPaginationConfig,
  actions,
  filterable,
  interactiveHeaders = true,
  rowCanExpand = () => false,
  excludePaginationNav = false,
  updateQueryDescription,
  withoutSideNav = false,
  columnSettingProps,
  timeoutError,
  queryDescription,
  currentView,
  savedViews,
  refetchSavedViews,
  refetchTable,
  clientSideColumnConfig,
  dataSourceComputationsAffectingQuery,
  rowIdField,
  totalCount,
  actionBarConfig,
  filterExclusionMatcher,
  hideViewOnMapButton = false,
}: FullWidthTableProps<T> & { css?: CSS }) => {
  const {
    resetRowSelection,
    rowSelection,
    onRowSelectionChange,
    setTotalCount,
    state,
    resetAndSetQueryDescription,
  } = useTableRowsSelectionState();

  useEffect(() => {
    resetAndSetQueryDescription(queryDescription);
  }, [queryDescription, resetAndSetQueryDescription]);

  useEffect(() => {
    setTotalCount(totalCount);
  }, [totalCount, setTotalCount]);

  const history = useHistory();
  const { user, admin, isPublic } = useContext(AuthContext);
  const { pathname } = useLocation();
  const { loading, loadingText, noDataText } = loadingDetails;
  const [localLoading, setLocalLoading] = useState(loading);
  const [scrollContainerHeight, setScrollContainerHeight] = useState("100%");
  const tableRef = useRef(null);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const [filterState, _] = useState<Array<FilterDescription>>(
    initialState.filters ?? [],
  );
  const [columnOrder, setColumnOrder] = useState<Array<string>>(() =>
    initialColumns.map(c => c.id!),
  );

  const handleSetColumnOrder = (
    order: string[] | ((prev: string[]) => string[]),
  ) => {
    setColumnOrder(order);
  };
  const [columns, setColumns] = useState(initialColumns);

  const { data: dependencyData, loading: loadingDependencies } =
    useGetSavedViewDependenciesQuery({
      skip: isPublic,
      fetchPolicy: "network-only",
    });

  useEffect(() => {
    if (savedViews) {
      setColumns(initialColumns);
      setColumnOrder(initialColumns.map(c => c.id!));
    }
  }, [currentView, setColumns, setColumnOrder, initialColumns, savedViews]);

  const [addingNewView, setAddingNewView] = useState(false);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  const defaultView = savedViews?.find(view => view.isDefault)!;

  const { id: tableId, name: tableName } = buildLocalTableInfo({
    entityId: user?.id ?? admin?.id,
    pathname,
  });

  const {
    getLocalTableState,
    setLocalColumnOrder,
    setLocalColumnSizing,
    setLocalSorting,
  } = useLocalTableDisplayConfig({
    tableId,
    defaultValue: {
      columnOrder: initialColumns.map(column => column.id!),
      columnSizing: {},
      sorting: [],
    },
  });

  const localTableConfig = getLocalTableState();

  const { columnSizing, sorting } = localTableConfig;

  const newColumnSizing = sanityCheckColumnSizing({
    columnSizing,
    columns,
  });

  const newSorting = sanityCheckSorting({ sorting, columns });

  const data = localLoading || loading ? (previousData ?? []) : currentData;

  const tableState: Partial<TableState> = {
    columnOrder,
    rowSelection,
  };

  if (manualPaginationConfig) {
    tableState.pagination = manualPaginationConfig.pagination;
  }

  const defaultColumn = {
    size: 150,
    maxSize: 400,
    minSize: 100,
  };

  const onFilterChange = (filters: Array<FilterDescription>) => {
    table.setPagination(prevPagination => ({
      ...prevPagination,
      pageIndex: 0,
    }));

    updateQueryDescription &&
      updateQueryDescription({ type: "updateFilters", data: filters });
  };

  const onColumnChange = (columnId: string, action: "add" | "remove") => {
    let newList = [...columns];

    if (action === "add") {
      const definition = columnSettingProps?.columnDefinitions.find(
        c => c.id === columnId,
      );

      if (definition) {
        newList.splice(-1, 0, definition);
        setColumns(newList);
        setColumnOrder(newList.map(c => c.id!));
      }
    } else {
      newList = columns.filter(column => column.id !== columnId);
      setColumns(newList);
      setColumnOrder(newList.map(c => c.id!));
    }

    if (updateQueryDescription) {
      updateQueryDescription({
        type: "updateFields",
        data: newList.map(c => c.id!),
      });
    }
  };

  // If a query description is provided, this means we should have manual sorting
  // (i.e. server-side sorting) enabled (true).
  // If the query description is not provided, we should have manual sorting
  // disabled (false), meaning `useReactTable` will handle sorting based on the
  // newSorting or sorting values.
  const manualSorting = !isNil(queryDescription);

  const table = useReactTable({
    data,
    columns,
    initialState: {
      columnSizing: newColumnSizing ?? columnSizing,
      sorting: newSorting ?? sorting,
      ...omit(initialState, "filters"),
      ...(excludePaginationNav ?
        {
          pagination: {
            pageIndex: 0,
            pageSize:
              DEFAULT_PAGE_SIZE > data.length ? DEFAULT_PAGE_SIZE : data.length,
          },
        }
      : {}),
    },
    state: tableState,
    manualSorting,
    getSortedRowModel: getSortedRowModel(),
    ...(manualPaginationConfig ?
      omit(manualPaginationConfig, "pagination")
    : {
        manualPagination: false,
        getPaginationRowModel: getPaginationRowModel(),
      }),
    onColumnOrderChange: setColumnOrder,
    autoResetPageIndex: false,
    enableExpanding: true,
    getRowCanExpand: rowCanExpand,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    columnResizeMode: "onChange",
    defaultColumn,
    getRowId: row => (row as any)[rowIdField],
    onRowSelectionChange,
  });

  const innerTableState = table.getState();

  const search = () =>
    filterable?.search({
      filters: filterState,
      sort: innerTableState.sorting,
      page: innerTableState.pagination.pageIndex + 1,
      columns,
    });

  useEffect(() => {
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryDescription, innerTableState.pagination]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setLocalLoading(false);
    }, 250);
    return () => clearTimeout(timeout);
  }, [loading]);

  const recalculateScrollHeight = useCallback(() => {
    if (!scrollableRef.current) return;

    const siblings = toArray(
      scrollableRef.current.parentElement?.children,
    ).filter(child => {
      const ignoredClassNames = ["scroll-wrapper", "loading-overlay"];
      return !toArray(child.classList).some(className =>
        ignoredClassNames.includes(className),
      );
    });

    const combinedHeightOfSiblings = siblings.reduce((totalHeight, element) => {
      return totalHeight + element.clientHeight;
    }, 0);

    setScrollContainerHeight(`calc(100vh - ${combinedHeightOfSiblings}px)`);
  }, []);

  useEffect(() => {
    recalculateScrollHeight();
  }, [
    scrollableRef.current?.parentElement?.children,
    loading,
    recalculateScrollHeight,
  ]);

  useEffect(() => {
    setLocalColumnSizing(innerTableState.columnSizing);
  }, [setLocalColumnSizing, innerTableState.columnSizing]);

  useEffect(() => {
    table.setPagination(prevPagination => ({
      ...prevPagination,
      pageIndex: 0,
    }));
    setLocalSorting(innerTableState.sorting);

    updateQueryDescription &&
      updateQueryDescription({
        type: "updateOrder",
        // we know that the table will always have at least one sorting column
        // in the case where a query description is used
        data: innerTableState.sorting as ColumnSortArray,
      });
    // Adding setLocalSorting to the dependencies would cause infinite re-renders on all new tables and old tables
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [innerTableState.sorting, updateQueryDescription, table]);

  const [hasValidVisibility, setHasValidVisibility] = useState(false);

  useEffect(() => {
    // Hidden views are always valid, because we don't have to worry about hidden columns/filters
    if (currentView?.hiddenFromPublic) {
      setHasValidVisibility(true);
    } else if (!loadingDependencies && !isPublic && queryDescription) {
      const { hasHiddenFields, hasHiddenTables } =
        checkSavedViewValidVisibility({
          queryDescriptions: [queryDescription],
          parcelImportConfig:
            dependencyData!.account!.parcelImportConfiguration ?? null,
          submissionTypes: dependencyData!.account!.submissionTypes,
          accountDocumentTypes: dependencyData!.account!.accountDocumentTypes,
          accountPropertyWarningDefinitions:
            dependencyData!.account!.accountPropertyWarningDefinitions,
          publicPortal: dependencyData!.account!.publicPortal,
        });

      setHasValidVisibility(!hasHiddenFields && !hasHiddenTables);
    }
  }, [
    loadingDependencies,
    queryDescription,
    dependencyData,
    isPublic,
    currentView?.hiddenFromPublic,
  ]);

  const onAddNewView = () => {
    if (updateQueryDescription && currentView) {
      const firstField = defaultView.query
        .fields[0] as SyntheticFieldDescription;

      updateQueryDescription({
        type: "initializeQueryDescription",
        data: { ...defaultView!.query, fields: [firstField] },
      });

      setColumns(initialColumns);
      setColumnOrder(initialColumns.map(c => c.id!));

      setAddingNewView(true);
    }
  };

  const onCancel = () => {
    if (updateQueryDescription && currentView) {
      updateQueryDescription({
        type: "initializeQueryDescription",
        data: currentView.query,
      });

      setColumns(initialColumns);
      setColumnOrder(initialColumns.map(c => c.id!));
      setAddingNewView(false);
    }
  };

  const hasViewUpdates = !isEqual(queryDescription, currentView?.query);
  const isLoading = loading || localLoading;

  return (
    <>
      <DataSyncBanner
        query={queryDescription}
        dataSourceComputationsAffectingQuery={
          dataSourceComputationsAffectingQuery
        }
      />
      {actionBarConfig?.objectType &&
        isSupportedBulkOperationObjectType(actionBarConfig.objectType) && (
          <BulkOperationProgressBanner
            objectType={actionBarConfig.objectType}
            onComplete={() => {
              resetRowSelection();
              refetchTable();
            }}
            onVisibleBannersChange={recalculateScrollHeight}
          />
        )}
      <TableContext.Provider
        value={{
          name: tableName,
          currentQuery: queryDescription,
          currentView,
          defaultView,
          columnConfig: clientSideColumnConfig,
        }}
      >
        <TableHeader isPublic={isPublic}>
          <FlexRow style={{ gap: "16px", alignItems: "center" }}>
            <FlexRow style={{ gap: "8px", alignItems: "center" }}>
              {savedViews && (
                <SavedViewsButton
                  savedViews={savedViews}
                  addingNewView={addingNewView}
                  onAddNewView={onAddNewView}
                />
              )}
              {filterable && filterable.filterConfiguration && (
                <NewFilters
                  config={filterable.filterConfiguration}
                  onFilterChange={onFilterChange}
                  addingNewView={addingNewView}
                  filterExclusionMatcher={filterExclusionMatcher}
                />
              )}
              {columnSettingProps && (
                <ColumnsButton
                  currentColumns={columns}
                  columnConfig={columnSettingProps.columnConfiguration}
                  hideColumn={id => onColumnChange(id, "remove")}
                  addColumn={id => onColumnChange(id, "add")}
                  addingNewView={addingNewView}
                />
              )}
            </FlexRow>
            {isLoading ?
              <Loading size="small" />
            : <Body
                size="default"
                type="regular"
                color="contentSecondary"
                testId="total-rows"
              >
                {totalCount}{" "}
                {pluralize({
                  count: totalCount,
                  options: { singular: "row", plural: "rows" },
                })}
              </Body>
            }
          </FlexRow>
          <FlexRow style={{ gap: "8px" }}>
            {currentView && !hasViewUpdates && !hideViewOnMapButton && (
              <Button
                size="small"
                styleVariant="secondary"
                leftIconName="earth"
                onClick={() => {
                  track("Clicked View on map", { savedViewId: currentView.id });
                  history.push({
                    state: { savedViewId: currentView.id },
                    pathname: buildLink(isPublic ? "explore" : "map"),
                  });
                }}
              >
                View on map
              </Button>
            )}
            {!hasViewUpdates && actions}
            {hasViewUpdates && !isPublic && (
              <SaveButton
                addingNewView={addingNewView}
                disabled={!hasValidVisibility}
                onSave={() => {
                  setAddingNewView(false);
                  refetchSavedViews();
                }}
                onCancel={onCancel}
              />
            )}
          </FlexRow>
        </TableHeader>
        <ScrollContainer
          scrollableRef={scrollableRef}
          height={scrollContainerHeight}
          loading={localLoading || loading}
          withoutSideNav={withoutSideNav}
          hasActionBar={state.totalSelected > 0}
        >
          <TableComponent
            data={data}
            columns={columns}
            isPublic={isPublic}
            table={table}
            innerTableState={innerTableState}
            columnSizing={columnSizing}
            defaultColumn={defaultColumn}
            setLocalColumnOrder={setLocalColumnOrder}
            columnOrder={columnOrder}
            interactiveHeaders={interactiveHeaders}
            openDropdownId={openDropdownId}
            setOpenDropdownId={setOpenDropdownId}
            setColumnOrder={handleSetColumnOrder}
            updateQueryDescription={updateQueryDescription}
            onColumnChange={onColumnChange}
            tableRef={tableRef}
          />
          {!localLoading &&
            !loading &&
            !timeoutError &&
            isEmpty(currentData) && <NoDataOverlay>{noDataText}</NoDataOverlay>}
          {(localLoading || loading) && (
            <LoadingOverlay className="loading-overlay">
              {loadingText}
            </LoadingOverlay>
          )}
          {timeoutError && (
            <TimeoutErrorState
              refetch={() =>
                filterable?.search({
                  filters: filterState,
                  sort: innerTableState.sorting,
                  page: innerTableState.pagination.pageIndex + 1,
                  columns,
                })
              }
            />
          )}

          <TableFooter>
            {!loading && !excludePaginationNav && (
              <PaginationSection
                paginationDetails={{
                  pageCount: table.getPageCount(),
                  pagination: innerTableState.pagination,
                  setPagination: table.setPagination,
                }}
                previousPage={table.previousPage}
                nextPage={table.nextPage}
                canGetPreviousPage={table.getCanPreviousPage}
                canGetNextPage={table.getCanNextPage}
              />
            )}
          </TableFooter>
        </ScrollContainer>
        {actionBarConfig && (
          <ActionBarWrapper
            actionBarConfig={actionBarConfig}
            refetchTable={refetchTable}
          />
        )}
      </TableContext.Provider>
    </>
  );
};

export const FullWidthTable = <T,>(
  props: FullWidthTableProps<T> & { children?: React.ReactNode },
) => {
  return (
    <TableRowsSelectionProvider>
      <FullWidthTableBase {...props} />
      {props.children}
    </TableRowsSelectionProvider>
  );
};
